/**
 * /app/$tenantSlug/payroll/filings/$id — G_FILE filing detail.
 *
 * Shows the generated filing's metadata + a download button (ECR/ESI/24Q)
 * or per-employee Part-B download list (Form-16), a "validate before you
 * file" hint naming the exact govt utility, and for 24Q the quarter's
 * challans (editable until the file is downloaded). Figures are immutable
 * (§128(1)); a downloaded filing can be REVISED — a new revision built from
 * the locked payroll and the employee records as they are now supersedes
 * it, and the downloaded one stays as it was (2026-10 re-test payroll #7).
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Download,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toApiError } from "@/lib/api";
import { formatPaise } from "@/lib/expense-format";
import { formatDate, formatDateTime } from "@/lib/format";
import { extensionForType } from "@/lib/download-filename";
import {
  WHOLE_QUARTER,
  challanAddedMessage,
  challanFromDraft,
  challanMonthLabel,
  defaultChallanMonth,
  filingFallbackName,
  quarterMonths,
} from "@/lib/filing-challans";
import { uanMissingFromError } from "@/lib/payroll-errors";
import type { UANMissingEmployee } from "@/types/payroll";
import { UANMissingAlert } from "@/components/payroll/employee-issue-alerts";
import { Form16ReleaseCard } from "@/components/payroll/form16-release";
import {
  useFiling,
  useChallans,
  useCreateChallan,
  useDeleteChallan,
  useReviseFiling,
  useRefreshAfterFilingDownload,
} from "@/hooks/use-filings";
import {
  downloadFilingFile,
  downloadForm16,
  form16SnapshotRows,
  tds24qPreWorkRightMonths,
  triggerDownload,
  FILING_KIND_LABELS,
} from "@/lib/api/filings";
import type { FilingDetail, TDSChallan } from "@/lib/api/filings";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/filings/$id",
)({
  component: FilingDetailRoute,
});

function FilingDetailRoute() {
  const { tenantSlug, id } = useParams({ strict: false }) as {
    tenantSlug: string;
    id: string;
  };
  const filingId = parseInt(id, 10);
  const q = useFiling(filingId);

  return (
    <PayrollModuleShell
      // The filing names the page: "PF ECR — 2026 / 9" (breadcrumb, tab title).
      title={
        q.data
          ? `${FILING_KIND_LABELS[q.data.filing.kind]} — ${q.data.filing.period_year}${
              q.data.filing.period_index > 0 ? ` / ${q.data.filing.period_index}` : ""
            }`
          : "Filing Detail"
      }
    >
      {/* Phones only: the desktop breadcrumb goes back to the filings. */}
      <Link
        to="/app/$tenantSlug/payroll/filings"
        params={{ tenantSlug }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground pointer-coarse:min-h-11 lg:hidden"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Filings
      </Link>

      {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {q.error && (
        <p className="text-sm text-destructive">{toApiError(q.error).message}</p>
      )}
      {q.data && <FilingBody detail={q.data} tenantSlug={tenantSlug} />}
    </PayrollModuleShell>
  );
}

function FilingBody({ detail, tenantSlug }: Readonly<{ detail: FilingDetail; tenantSlug: string }>) {
  const { filing } = detail;
  const [downloading, setDownloading] = useState(false);
  const refreshAfterDownload = useRefreshAfterFilingDownload();
  const superseded = detail.superseded_by;
  const blankIP = filing.kind === "esi_monthly" ? (detail.blank_ip_numbers ?? []) : [];

  async function onDownload() {
    setDownloading(true);
    try {
      const blob = await downloadFilingFile(filing.id, { download: true });
      // The server's name (ecr-….txt / esi-monthly-….csv / 24Q-….txt);
      // the fallback keeps the response type's extension.
      triggerDownload(blob, filingFallbackName(filing, extensionForType(blob.type)));
      toast.success(`${FILING_KIND_LABELS[filing.kind]} file downloaded.`);
      // The first download freezes it: the revise card appears, the 24Q's
      // challans lock.
      if (!filing.frozen) void refreshAfterDownload();
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">
                {FILING_KIND_LABELS[filing.kind]} — {filing.period_year}
                {filing.period_index > 0 ? ` / ${filing.period_index}` : ""}
              </CardTitle>
              <CardDescription>
                Generated {formatDateTime(filing.generated_at)}
                {filing.revision_no > 0 ? ` · revision ${filing.revision_no}` : ""}
              </CardDescription>
            </div>
            {/* "Downloaded", as on the hub: generating or downloading a
                file is not filing it (re-test payroll #7). */}
            <StatusBadge
              status={filing.frozen ? "frozen" : "generated"}
              label={filing.frozen ? "Downloaded (Frozen)" : undefined}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {superseded && (
            <Alert data-testid="filing-superseded">
              <History aria-hidden className="h-4 w-4" />
              <AlertTitle>Revision {superseded.revision_no} replaces this filing</AlertTitle>
              <AlertDescription>
                This revision is kept as it was downloaded.{" "}
                <Link
                  to="/app/$tenantSlug/payroll/filings/$id"
                  params={{ tenantSlug, id: String(superseded.id) }}
                  className="font-medium underline underline-offset-4"
                >
                  Open revision {superseded.revision_no}
                </Link>{" "}
                to download the file that counts.
              </AlertDescription>
            </Alert>
          )}
          {blankIP.length > 0 && (
            <Alert variant="destructive" data-testid="filing-blank-ip">
              <AlertCircle aria-hidden className="h-4 w-4" />
              <AlertTitle>
                This file has no IP number for {blankIP.length === 1 ? blankIP[0] : `${blankIP.length} insured persons`}
              </AlertTitle>
              <AlertDescription className="space-y-1">
                {blankIP.length > 1 && <p>{blankIP.join(", ")}.</p>}
                <p>
                  It was downloaded before blank IP numbers were refused, and the ESIC portal rejects these lines.
                  Add each one&apos;s ESI IP number on their employee record, then generate a revised file
                  {superseded ? " — a revision already exists." : " below."}
                </p>
              </AlertDescription>
            </Alert>
          )}
          <Alert>
            <AlertTitle>Validate before you file</AlertTitle>
            <AlertDescription>{validateHint(filing.kind)}</AlertDescription>
          </Alert>
          {filing.kind !== "form16" ? (
            <Button onClick={onDownload} disabled={downloading} className="gap-1.5">
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download File
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Form-16 is a per-employee PDF. Use the snapshot table below to
              download each employee's Part B; obtain Part A from TRACES and
              merge before issuing.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Form 16 has no downloaded file: it freezes when it is released
          to employees, and is corrected by a revision released again. */}
      {(filing.kind === "form16" ? !!filing.released_at : filing.frozen) && !superseded && (
        <ReviseCard detail={detail} tenantSlug={tenantSlug} />
      )}

      {filing.kind === "tds_24q_fvu" && tds24qPreWorkRightMonths(filing).length > 0 && (
        <Alert data-testid="filing-24q-pre-workright">
          <AlertTitle>Months paid before WorkRight are not in this file</AlertTitle>
          <AlertDescription>
            {tds24qPreWorkRightMonths(filing).join(", ")} were paid before payroll moved to
            WorkRight. Their TDS deductions and challans are not in this 24Q — add them in the
            RPU from your previous payroll records before you run the FVU, so the quarter&apos;s
            return is complete.
          </AlertDescription>
        </Alert>
      )}

      {filing.kind === "tds_24q_fvu" && (
        <ChallansPanel detail={detail} />
      )}

      {filing.kind === "form16" && (
        <Form16ReleaseCard
          filing={filing}
          employeeCount={form16SnapshotRows(filing).length}
          superseded={!!superseded}
        />
      )}
      {filing.kind === "form16" && <Form16EmployeesPanel detail={detail} />}
    </div>
  );
}

/**
 * Generate a revised file for a downloaded filing (2026-10 re-test payroll
 * #7). A new revision is built from the period's locked payroll and the
 * employee records as they are now; the downloaded one is never changed.
 */
function ReviseCard({ detail, tenantSlug }: Readonly<{ detail: FilingDetail; tenantSlug: string }>) {
  const { filing } = detail;
  const navigate = useNavigate();
  const revise = useReviseFiling();
  const [problem, setProblem] = useState<string | null>(null);
  const [uanMissing, setUANMissing] = useState<UANMissingEmployee[] | null>(null);
  const label = FILING_KIND_LABELS[filing.kind];

  async function onRevise() {
    setProblem(null);
    setUANMissing(null);
    try {
      const next = await revise.mutateAsync(filing.id);
      toast.success(`Revision ${next.revision_no} generated. Download it and validate it before uploading.`);
      void navigate({
        to: "/app/$tenantSlug/payroll/filings/$id",
        params: { tenantSlug, id: String(next.id) },
      });
    } catch (err) {
      const missing = uanMissingFromError(err);
      if (missing) {
        setUANMissing(missing);
        return;
      }
      const msg = toApiError(err).message;
      setProblem(msg);
      toast.error(msg);
    }
  }

  return (
    <Card data-testid="filing-revise">
      <CardHeader>
        <CardTitle className="text-base">Need to change this file?</CardTitle>
        <CardDescription>
          {filing.kind === "form16" ? (
            <>
              This Form 16 has been released to employees, so its figures are frozen. If a figure
              was wrong, generate a revised Form 16: it is built from the year&apos;s locked payroll
              and the employee records as they are now, starts with this one&apos;s corrections, and
              reaches employees only when you release it.
            </>
          ) : (
            <>
              This {label} file has been downloaded, so its figures are frozen. If the portal or the
              validation utility rejected it, or an employee record was wrong, generate a revised file:
              it is built from the period&apos;s locked payroll and the employee records as they are now.
              {filing.kind === "tds_24q_fvu" ? " The quarter's challans can be edited again for the revision." : ""}
            </>
          )}
        </CardDescription>
        {reviseCaution(filing.kind) && (
          <p className="text-xs text-muted-foreground" data-testid="filing-revise-caution">
            {reviseCaution(filing.kind)}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="gap-1.5" disabled={revise.isPending}>
              {revise.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {filing.kind === "form16" ? "Generate a Revised Form 16" : "Generate a Revised File"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Generate Revision {filing.revision_no + 1}?</AlertDialogTitle>
              <AlertDialogDescription>
                Revision {filing.revision_no + 1} replaces this one as the {label} for the period. This
                revision and the file you downloaded stay exactly as they are. You may be asked for your
                two-factor code.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => void onRevise()}>Generate Revision</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {problem && (
          <p role="alert" className="text-sm text-destructive" data-testid="filing-revise-error">
            {problem}
          </p>
        )}
        {uanMissing && <UANMissingAlert employees={uanMissing} tenantSlug={tenantSlug} />}
      </CardContent>
    </Card>
  );
}

/** Form 16 Part B, one PDF per employee in the frozen snapshot. */
function Form16EmployeesPanel({ detail }: Readonly<{ detail: FilingDetail }>) {
  const { filing } = detail;
  const rows = form16SnapshotRows(filing);
  const [busy, setBusy] = useState<number | null>(null);

  async function onDownload(employeeId: number, name: string) {
    setBusy(employeeId);
    try {
      const blob = await downloadForm16(filing.id, employeeId, { download: true });
      // The server names it by employee code (form16-<FY>-<code>.pdf);
      // the fallback carries no display name either.
      triggerDownload(blob, `form16-partb-${filing.period_year}-${employeeId}.pdf`);
      toast.success(`Form 16 Part B for ${name || `employee #${employeeId}`} downloaded.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Employees</CardTitle>
        <CardDescription>Part B for each employee paid in the year.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState title="No employees in this filing." />
        ) : (
          <div className="divide-y rounded border">
            {rows.map((r) => (
              <div key={r.employee_id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{r.employee_name || `Employee #${r.employee_id}`}</span>
                  <span className="text-muted-foreground">
                    {" "}· PAN {r.pan || "—"} · salary {formatPaise(r.gross_paise)} · TDS {formatPaise(r.tds_paise)}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={busy === r.employee_id}
                  onClick={() => void onDownload(r.employee_id, r.employee_name)}
                  data-testid={`form16-partb-${r.employee_id}`}
                >
                  {busy === r.employee_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Part B
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The quarter's TDS challans — the CD records of the 24Q (2026-10 re-test
 * payroll #1). Each challan says which month's TDS it paid: the 24Q links a
 * month's deductions to that month's challan, so a quarter deposited month
 * by month needs one challan per month. Editable until the file is
 * downloaded; after that only a revised 24Q makes them editable again.
 */
function ChallansPanel({ detail }: Readonly<{ detail: FilingDetail }>) {
  const { filing } = detail;
  const year = filing.period_year;
  const quarter = filing.period_index;
  const q = useChallans(year, quarter);
  const createMut = useCreateChallan();
  const delMut = useDeleteChallan();
  const items = q.data?.items ?? [];
  // The file carries the challans as they were at the download.
  const locked = filing.frozen || detail.superseded_by != null;
  const months = quarterMonths(year, quarter);

  const [bsr, setBsr] = useState("");
  const [serial, setSerial] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [month, setMonth] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // Default to the first month without a challan (else the whole quarter,
  // never "" — the select stays controlled); the user's pick wins.
  const monthValue = month ?? defaultChallanMonth(year, quarter, items);

  async function onAdd() {
    const parsed = challanFromDraft({ bsr, serial, depositDate: date, amount, month: monthValue });
    if (!parsed.ok) {
      setFormError(parsed.problem);
      return;
    }
    setFormError(null);
    try {
      await createMut.mutateAsync({ period_year: year, period_quarter: quarter, ...parsed.challan });
      setBsr("");
      setSerial("");
      setDate("");
      setAmount("");
      setMonth(null);
      toast.success(challanAddedMessage(parsed.challan.challan_serial, parsed.challan.period_month, year));
    } catch (err) {
      const msg = toApiError(err).message;
      setFormError(msg);
      toast.error(msg);
    }
  }

  async function onDelete(c: TDSChallan) {
    try {
      await delMut.mutateAsync(c.id);
      toast.success(`Challan ${c.challan_serial} deleted.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card data-testid="filing-challans">
      <CardHeader>
        <CardTitle className="text-base">TDS Challans — Q{quarter} {year}</CardTitle>
        <CardDescription>
          The bank challans that deposited this quarter&apos;s TDS — they become the challan records in
          the 24Q. Add one per month you deposited (set the month whose TDS it paid), or a single challan
          for the whole quarter if you deposited it at once.{" "}
          {detail.superseded_by
            ? `These are the quarter's challans as they are now — revision ${detail.superseded_by.revision_no} carries them; open it to change them.`
            : locked
              ? "This 24Q has been downloaded, so its challans are frozen with that file — generate a revised 24Q above to change them."
              : "You can change them until the file is downloaded."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!locked && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
              <div>
                <Label htmlFor="challan-month">Month Paid</Label>
                <Select value={monthValue} onValueChange={(v) => setMonth(v)}>
                  <SelectTrigger id="challan-month" data-testid="challan-month">
                    <SelectValue placeholder="Choose" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.month} value={String(m.month)}>
                        {m.label}
                      </SelectItem>
                    ))}
                    <SelectItem value={WHOLE_QUARTER}>Whole Quarter (One Challan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="bsr">BSR Code</Label>
                <Input
                  id="bsr"
                  inputMode="numeric"
                  maxLength={7}
                  value={bsr}
                  onChange={(e) => setBsr(e.target.value)}
                  placeholder="0123456"
                />
              </div>
              <div>
                <Label htmlFor="serial">Challan Serial</Label>
                <Input
                  id="serial"
                  inputMode="numeric"
                  maxLength={5}
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="00042"
                />
              </div>
              <div>
                <Label htmlFor="date">Deposit Date</Label>
                <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="amount">TDS (₹)</Label>
                <Input
                  id="amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="12500.00"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={() => void onAdd()} disabled={createMut.isPending} className="w-full gap-1">
                  {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add
                </Button>
              </div>
            </div>
            {formError && (
              <p role="alert" className="text-sm text-destructive" data-testid="challan-form-error">
                {formError}
              </p>
            )}
          </div>
        )}

        {q.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : q.error ? (
          <Alert variant="destructive">
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        ) : items.length === 0 ? (
          <EmptyState title="No challans yet." />
        ) : (
          <div className="divide-y rounded border">
            {items.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{challanMonthLabel(c.period_month, year)}</span>
                  <span className="text-muted-foreground">
                    {" "}· BSR {c.bsr_code} · serial {c.challan_serial} · deposited {formatDate(c.deposit_date)} ·{" "}
                  </span>
                  <span className="tabular-nums">{formatPaise(c.tds_paise)}</span>
                </span>
                {!locked && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={delMut.isPending}
                        aria-label={`Delete challan ${c.challan_serial}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Challan {c.challan_serial}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          BSR {c.bsr_code}, deposited {formatDate(c.deposit_date)}, {formatPaise(c.tds_paise)} for{" "}
                          {challanMonthLabel(c.period_month, year)}. It is removed from this quarter&apos;s 24Q; add it
                          again if you deleted it by mistake.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep It</AlertDialogCancel>
                        <AlertDialogAction
                          className={cn(buttonVariants({ variant: "destructive" }))}
                          onClick={() => void onDelete(c)}
                        >
                          Delete Challan
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** A revised file replaces one that was never accepted; once a return is
 *  filed, the authority takes changes its own way. */
function reviseCaution(kind: string): string {
  switch (kind) {
    case "tds_24q_fvu":
      return "Only for a 24Q the TIN facilitation centre has not accepted. Once a 24Q is accepted, changes are filed as a correction statement prepared from the consolidated file on TRACES.";
    case "ecr":
      return "Only for an ECR whose challan is not yet paid. After payment, EPFO takes changes as a supplementary (arrear) ECR.";
    default:
      return "";
  }
}

function validateHint(kind: string): string {
  switch (kind) {
    case "ecr":
      return "Upload this .txt on the EPFO Unified Portal (ECR Upload). The portal returns a TRRN and a draft challan.";
    case "esi_monthly":
      // Re-test payroll #10: "31 days" beside a slip's "Paid Days 30.5 of
      // 31" read as a mistake. ESIC takes whole days and a day paid in
      // part is a day for which wages were paid (esi_monthly.go rounds a
      // fraction up) — say so.
      return "Upload this CSV via the ESIC employer portal Monthly Contribution bulk upload; the portal computes the 0.75% / 3.25% contributions. ESIC takes whole days: a day paid in part (a half-day loss of pay) counts as a day wages were paid, so 30.5 paid days are reported as 31.";
    case "tds_24q_fvu":
      return "Open this .txt in the Protean RPU, then run the FVU. Upload the resulting .fvu from your TRACES/e-filing login.";
    case "form16":
      return "This is Part B only. Download Part A from TRACES after the 24Q is processed and merge before issuing to each employee.";
    default:
      return "";
  }
}
